// ---------------------------------------------------------------------------
// revealObserve — the per-clash snapshots a reveal plays back
// ---------------------------------------------------------------------------
//
// resolveRound's `observe.afterCorridor` hook sees the running Grids at the
// moment each corridor's crater lands. This builds the observer the reveal
// needs from it (lifted out of useGameState so the tutorial plays its rounds
// back exactly as a war does). Display only: the engine result is untouched.
//
// Per clash it adds to the event:
//   selfLedgerAtClash / oppLedgerAtClash  the brood ledger each side FOUGHT
//       with (a brood cratered by an earlier corridor fires fewer shots), and
//       whether its broods fired (a covered defence holds them back)
//   postClashOppHf / postClashSelfHf      the struck Grid after this crater
//   craterStages                          its intermediate snapshots, one per
//       landing shot, so every impact buys its ring of the footprint

import { basesOf } from './gameLogic.js'
import { cloneHomefront } from './subplotGrid.js'

// A clash snapshot for column `fromCol` keeps every fired brood whose clash
// hasn't finished (col >= fromCol). Once its clash has played, a fired brood
// leaves entirely, egg and all. Always a COPY: the rules keep mutating the
// running Grid in place as later corridors clear (retiring their fired
// broods), so a live reference would show those broods gone before their own
// clash fires.
export function graftPendingFiredBases(hf, fired, fromCol) {
    const pending = fired.filter(f => f.col >= fromCol && !hf.broods?.[f.st.id])
    const g = cloneHomefront(hf)
    for (const { st } of pending) {
        g.broods[st.id] = st
        for (const s of st.footprint) {
            const cell = g.subplots[s]
            if (cell && !cell.destroyed && cell.broodId == null) cell.broodId = st.id
        }
    }
    return g
}

// The brood ledger a corridor fought with.
export function ledgerOf(bf) {
    return {
        bases: basesOf(bf),
        fortCount: bf.fortCount || 0,
        fortInConstruction: bf.fortInConstruction || 0,
    }
}

export function makeRevealObserve() {
    return {
        afterCorridor: ({ col, event, stageHomefronts, selfHomefront, oppHomefront, selfFiredBases, oppFiredBases, selfBf, oppBf }) => {
            const extra = {
                selfLedgerAtClash: { ...ledgerOf(selfBf), broodsFire: event.selfBroodsFired !== false },
                oppLedgerAtClash: { ...ledgerOf(oppBf), broodsFire: event.oppBroodsFired !== false },
            }
            if (event.killSide === 'opp') {
                extra.postClashOppHf = graftPendingFiredBases(oppHomefront, oppFiredBases, col)
                extra.craterStages = stageHomefronts.map(hf => graftPendingFiredBases(hf, oppFiredBases, col))
            } else if (event.killSide === 'self') {
                extra.postClashSelfHf = graftPendingFiredBases(selfHomefront, selfFiredBases, col)
                extra.craterStages = stageHomefronts.map(hf => graftPendingFiredBases(hf, selfFiredBases, col))
            }
            return extra
        },
    }
}
